/**
 * EntityPicker 의 순수 함수 (디자인 정본 B11).
 *
 * ★★★ **검색은 «받아 둔 트리 안에서» 한다 — 서버에 다시 묻지 않는다.**
 *    지금 사람을 고르는 자리 다섯 곳은 전부 글자마다 `GET /users?search=` 를 부른다.
 *    그 질의는 전체 사용자 표를 훑고, **사람이 늘수록 느려진다.**
 *    게다가 **이름을 알아야만 찾을 수 있어** 새로 온 사람·다른 부서 사람·동명이인을 못 고른다.
 */

export interface PickerPerson {
  id: string;
  name: string;
  /** 직위·소속처럼 «누구인지 가리는» 한 줄. 동명이인은 이걸로 갈린다. */
  sub?: string;
  /** 검색에 걸리게 할 여분의 글자(사번·메일 등). 화면에는 안 보인다. */
  keywords?: string;
  disabled?: boolean;
}

export interface PickerGroup {
  id: string;
  name: string;
  /** 이 묶음에 바로 속한 사람들. */
  people?: ReadonlyArray<PickerPerson>;
  children?: ReadonlyArray<PickerGroup>;
}

/** 고른 것 — 사람만이 아니라 **팀째·부서째**도 고를 수 있다(정본 B11). */
export type PickedKind = 'person' | 'group';
export interface Picked {
  kind: PickedKind;
  id: string;
  name: string;
  sub?: string;
}

const norm = (s: string) => s.toLowerCase().trim();

/** 사람 한 명이 검색 글자에 걸리나. 이름만이 아니라 소속·직위·사번까지 본다. */
export function personMatches(p: PickerPerson, q: string): boolean {
  if (!q) return true;
  return [p.name, p.sub, p.keywords].filter(Boolean).join(' ').toLowerCase().includes(norm(q));
}

/**
 * 검색어로 트리를 거른다.
 *
 * ★★ **묶음 이름이 걸리면 그 안 사람을 «전부» 남긴다** — 「기획팀」을 치면 기획팀 전원이 나와야지
 *    이름에 「기획」이 든 사람만 나오면 안 된다.
 * ★ 아무것도 안 남은 가지는 지운다 — 빈 폴더가 줄줄이 남으면 결과를 못 읽는다.
 */
export function filterGroups(groups: ReadonlyArray<PickerGroup>, q: string): PickerGroup[] {
  if (!q.trim()) return groups as PickerGroup[];
  const out: PickerGroup[] = [];
  for (const g of groups) {
    const groupHit = norm(g.name).includes(norm(q));
    const people = groupHit ? (g.people ?? []) : (g.people ?? []).filter((p) => personMatches(p, q));
    const children = filterGroups(g.children ?? [], groupHit ? '' : q);
    if (groupHit || people.length > 0 || children.length > 0) {
      out.push({ ...g, people: [...people], children });
    }
  }
  return out;
}

/** 그 묶음(아래 가지 포함)에 있는 사람 전부 — 「팀째 고르기」가 쓴다. */
export function peopleOf(group: PickerGroup): PickerPerson[] {
  const out: PickerPerson[] = [...(group.people ?? [])];
  for (const c of group.children ?? []) out.push(...peopleOf(c));
  return out;
}

/** 트리 전체의 사람 수 — 「전원 N명」 같은 안내에 쓴다. */
export function countPeople(groups: ReadonlyArray<PickerGroup>): number {
  return groups.reduce((n, g) => n + peopleOf(g).length, 0);
}

/** 고른 것들에서 사람 id 만 — 팀째 고른 것은 그 안 사람으로 펼친다. */
export function expandToPersonIds(picked: ReadonlyArray<Picked>, groups: ReadonlyArray<PickerGroup>): string[] {
  const byGroup = new Map<string, PickerGroup>();
  const walk = (list: ReadonlyArray<PickerGroup>) => {
    for (const g of list) { byGroup.set(g.id, g); walk(g.children ?? []); }
  };
  walk(groups);

  const ids = new Set<string>();
  for (const p of picked) {
    if (p.kind === 'person') ids.add(p.id);
    else {
      const g = byGroup.get(p.id);
      if (g) for (const person of peopleOf(g)) if (!person.disabled) ids.add(person.id);
    }
  }
  return [...ids];
}

/** 이미 담겼나. 사람과 묶음은 id 가 겹칠 수 있어 종류까지 본다. */
export function isPicked(picked: ReadonlyArray<Picked>, kind: PickedKind, id: string): boolean {
  return picked.some((p) => p.kind === kind && p.id === id);
}

export function togglePicked(picked: ReadonlyArray<Picked>, next: Picked, multiple: boolean): Picked[] {
  if (!multiple) return isPicked(picked, next.kind, next.id) ? [] : [next];
  return isPicked(picked, next.kind, next.id)
    ? picked.filter((p) => !(p.kind === next.kind && p.id === next.id))
    : [...picked, next];
}
